import { useEffect, useRef } from 'react';

/**
 * Hook that returns a ref which always contains the latest value.
 * Useful for avoiding stale closures in event handlers or async operations.
 *
 * @example
 * ```ts
 * const onSuccess = useCallback(() => {
 *   // This callback changes frequently based on props/state
 *   showToast(message);
 * }, [showToast, message]);
 *
 * const onSuccessRef = useLatestRef(() => {
 *   // This callback will always reference the latest values by the time it is called
 *   showToast(message);
 * });
 *
 * const handleSubmit = useCallback(async (data) => {
 *   await submitForm(data);
 *   onSuccessRef.current(); // Always calls latest onSuccess
 * }, []); // Empty deps - no need to update when onSuccess changes
 * ```
 */
export function useLatestRef<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  });

  return ref;
}
